import React from "react";
import Modal from "../../../../components/Modal";
import Icon from "../../../../components/Icon";

/**
 * ApproveReviewModal
 *
 * Extracted from EditClientModal — zero logic changes.
 * All state and handlers are passed in as props.
 *
 * Props:
 *  isOpen               - bool
 *  onClose              - () => void
 *  documents            - array from clientData.documents
 *  approvedFilePaths    - Set<string>
 *  viewedFilePaths      - Set<string>
 *  allUploadedFilePaths - string[]
 *  pendingDocCount      - number
 *  allDocsViewed        - bool
 *  approveReady         - bool
 *  isActionLoading      - bool
 *  handleViewDocument   - (filePath) => void
 *  handleDocApprove     - (doc, file) => void
 *  handleApproveConfirm - () => void
 */
const ApproveReviewModal = ({
  isOpen,
  onClose,
  documents,
  approvedFilePaths,
  viewedFilePaths,
  allUploadedFilePaths,
  pendingDocCount,
  allDocsViewed,
  approveReady,
  hasAnyDeficiency,
  isActionLoading,
  handleViewDocument,
  handleDocApprove,
  handleApproveConfirm,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title="Review & Approve Documents"
    size="medium"
  >
    <div className="approve-review">
      {/* Progress header */}
      <div className="approve-review__progress-header">
        <span className="approve-review__progress-header-title">
          Document Review
        </span>
        <span className="approve-review__progress-header-count">
          {approvedFilePaths.size} / {allUploadedFilePaths.length} approved
          {pendingDocCount > 0 && ` · ${pendingDocCount} pending`}
        </span>
      </div>
      {allUploadedFilePaths.length > 0 && (
        <div className="approve-review__progress-bar">
          <div
            className="approve-review__progress-bar-fill"
            style={{
              width: `${(approvedFilePaths.size / allUploadedFilePaths.length) * 100}%`,
            }}
          />
        </div>
      )}
      <p className="approve-review__hint">
        View each uploaded document then approve it one by one. All documents
        must be uploaded and approved to confirm.
      </p>

      {/* Full document list — uploaded + pending */}
      <ul className="approve-review__list">
        {documents.map((doc) => {
          const uploadedFiles =
            doc.files?.filter((f) => f.isUploaded && f.filePath) || [];
          const isPending = uploadedFiles.length === 0;

          if (isPending) {
            return (
              <li key={doc.key} className="approve-review__item">
                <div
                  className="approve-review__item-dot"
                  style={{ background: "#fef3c7", color: "#d97706" }}
                >
                  ⚠
                </div>
                <div className="approve-review__item-info">
                  <span className="approve-review__item-name">
                    {doc.documentName}
                  </span>
                  <span
                    className="approve-review__item-file"
                    style={{ color: "#d97706" }}
                  >
                    Pending upload
                  </span>
                </div>
                <div className="approve-review__item-actions">
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "#d97706",
                      fontWeight: 600,
                    }}
                  >
                    Not uploaded
                  </span>
                </div>
              </li>
            );
          }

          // this is one by one documents approved as per validated
          return uploadedFiles.map((file, fi) => {
            const isApproved = approvedFilePaths.has(file.filePath);
            const isViewed = viewedFilePaths.has(file.filePath);
            const isDeficient = doc.isDeficient === true;
            return (
              <li
                key={`${doc.key}-${fi}`}
                className={`approve-review__item ${isApproved ? "approve-review__item--approved" : ""} ${isDeficient ? "approve-review__item--deficient" : ""}`}
              >
                <div
                  className={`approve-review__item-dot ${isApproved ? "approve-review__item-dot--approved" : ""} ${isDeficient ? "approve-review__item-dot--deficient" : ""}`}
                >
                  {isApproved ? "✓" : isDeficient ? "!" : fi + 1}
                </div>
                <div className="approve-review__item-info">
                  <span className="approve-review__item-name">
                    {doc.documentName}
                  </span>
                  <span className="approve-review__item-file">
                    📄 {file.fileName}
                  </span>
                  {isDeficient && (
                    <span className="approve-review__item-deficiency-msg">
                      ⚠ Deficiency raised — cannot be approved
                    </span>
                  )}
                </div>
                <div className="approve-review__item-actions">
                  <button
                    type="button"
                    className={`view-doc-btn ${isViewed ? "view-doc-btn--viewed" : ""}`}
                    onClick={() =>
                      handleViewDocument(file.filePath, file.fileName)
                    }
                    title={isViewed ? "Already viewed" : "View document"}
                  >
                    <Icon iconName="view" width={13} height={13} />
                    {isViewed ? "Viewed" : "View"}
                  </button>
                  {isDeficient ? (
                    <span className="approve-review__item-deficient-badge">
                      ✗ Deficient
                    </span>
                  ) : isApproved ? (
                    <span className="approve-review__item-approved-badge">
                      ✓ Approved
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="doc-action-btn doc-action-btn--approve"
                      disabled={!isViewed}
                      title={
                        isViewed
                          ? "Approve this document"
                          : "View document first"
                      }
                      onClick={() => handleDocApprove(doc, file)}
                    >
                      ✓ Approve
                    </button>
                  )}
                </div>
              </li>
            );
          });
        })}
      </ul>

      {/* Footer */}
      <div className="approve-review__actions">
        <span className="approve-review__actions-status">
          {approveReady ? (
            <strong>All documents approved — ready to submit</strong>
          ) : hasAnyDeficiency ? (
            <span style={{ color: "#dc2626", fontWeight: 600 }}>
              ✗ One or more documents have deficiencies — resolve them before
              approving
            </span>
          ) : pendingDocCount > 0 ? (
            `${pendingDocCount} document(s) still pending upload`
          ) : !allDocsViewed ? (
            `View all documents first (${viewedFilePaths.size}/${allUploadedFilePaths.length} viewed)`
          ) : (
            `Approve each document (${approvedFilePaths.size}/${allUploadedFilePaths.length} approved)`
          )}
        </span>
        <div className="approve-review__actions-btns">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-submit-approve"
            disabled={!approveReady || isActionLoading}
            onClick={handleApproveConfirm}
          >
            {isActionLoading ? "Approving..." : "Confirm Approve"}
          </button>
        </div>
      </div>
    </div>
  </Modal>
);

export default ApproveReviewModal;
